import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import clsx from "clsx";

export type ThemePreference = "system" | "dark" | "light";
export type ResolvedTheme = "dark" | "light";

type ThemeContextValue = {
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setTheme: (next: ThemePreference) => void;
  toggleTheme: () => void;
};

const STORAGE_KEY = "px7-theme-preference";
const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function getInitialTheme(): ThemePreference {
  if (typeof window === "undefined") return "dark";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light" || stored === "system") return stored;
  return "system";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemePreference>(() => getInitialTheme());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(prefers-color-scheme: light)");
    const sync = () => setSystemTheme(media.matches ? "light" : "dark");
    sync();

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }

    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  const resolvedTheme = theme === "system" ? systemTheme : theme;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.dataset.theme = resolvedTheme;
    root.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (theme === "system") {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme: () => {
        setTheme(resolvedTheme === "dark" ? "light" : "dark");
      },
    }),
    [theme, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

export function ThemeToggle({ compact = false, className = "" }: { compact?: boolean; className?: string }) {
  const { resolvedTheme, toggleTheme } = useTheme();
  const nextTheme = resolvedTheme === "dark" ? "light" : "dark";
  const compactTone = resolvedTheme === "dark" ? "bg-px-green" : "bg-px-green-dark";

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={clsx(
        "rounded-full border border-px-line bg-px-surface/88 text-px-text shadow-card backdrop-blur-xl transition hover:border-px-green/40 hover:bg-px-surface-hi",
        compact ? "inline-flex items-center gap-2 px-3 py-2 text-xs" : "inline-flex items-center gap-3 px-4 py-3 text-sm",
        className,
      )}
      aria-label={`Switch to ${nextTheme} mode`}
      title={`Switch to ${nextTheme} mode`}
    >
      {compact ? (
        <>
          <span className={clsx("size-2 rounded-full", compactTone)} />
          <span className="font-semibold uppercase tracking-[0.14em]">{resolvedTheme}</span>
        </>
      ) : (
        <>
          <span className="text-[10px] uppercase tracking-[0.18em] text-px-text-muted">Theme mode</span>
          <span className="font-semibold">{resolvedTheme === "dark" ? "Dark" : "Light"}</span>
        </>
      )}
    </button>
  );
}
